import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DatePickerModule } from 'primeng/datepicker';
import { InputMaskModule } from 'primeng/inputmask';
import { SelectButtonModule } from 'primeng/selectbutton';
import { AutoCompleteCompleteEvent, AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TranslationPipe } from '../../../pipes/translation.pipe';
import { FileUploadModule } from 'primeng/fileupload';
import { DateTime } from 'luxon';
import {
  CreateTreatmentRequestDto,
  CustomerClient,
  CustomerDto,
  DocumentDto,
  PetClient,
  PetDto,
  TreatmentClient
} from '../../../api/api-client';
import { FeedbackDataService } from '../../../services/feedback-data.service';
import { Editor } from 'primeng/editor';
import { isFormControlInvalid } from '../../../util/form-control-invalid-checker';
import { SubscriptionManager } from '../../../services/subscription-manager.service';


@Component({
  selector: 'app-create-treatment',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    FileUploadModule,
    DatePickerModule,
    InputMaskModule,
    SelectButtonModule,
    AutoCompleteModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    ButtonModule,
    RouterModule,
    TranslationPipe,
    Editor
  ],
  providers: [SubscriptionManager],
  templateUrl: './create-treatment.component.html',
  styleUrl: './create-treatment.component.scss'
})
export class CreateTreatmentComponent implements OnInit {
  createTreatmentForm = new FormGroup({
    description: new FormControl('', { nonNullable: true }),
    title: new FormControl<string>('', Validators.required),
    appointmentDate: new FormControl<Date>(new Date(Date.now()), { nonNullable: true }),
    pet: new FormControl<PetDto | null>(null, [Validators.required]),
    images: new FormControl<DocumentDto[]>([], { nonNullable: true }),
    owner: new FormControl<CustomerDto | null>(null, [Validators.required])
  });

  customers!: CustomerDto[];
  customersSuggestions!: CustomerDto[];
  pets!: PetDto[];
  petsSuggestions!: PetDto[];
  isOwnerSet = false;
  isPetSet = false;

  calledFromDashboard = false;
  submitButtonClicked: boolean = false;

  constructor(
    private readonly subscriptionManager: SubscriptionManager,
    private readonly customerService: CustomerClient,
    private readonly route: ActivatedRoute,
    private readonly petService: PetClient,
    private readonly treatmentService: TreatmentClient,
    private readonly router: Router,
    private readonly feedbackService: FeedbackDataService,
    private readonly translationPipe: TranslationPipe
  ) {
    let currentDateTimeClamped = DateTime.now();
    if (currentDateTimeClamped.minute < 15) {
      currentDateTimeClamped = currentDateTimeClamped.set({ minute: 15 });
    } else if (currentDateTimeClamped.minute < 30) {
      currentDateTimeClamped = currentDateTimeClamped.set({ minute: 30 });
    } else if (currentDateTimeClamped.minute < 45) {
      currentDateTimeClamped = currentDateTimeClamped.set({ minute: 45 });
    } else {
      currentDateTimeClamped = currentDateTimeClamped.set({ minute: 0, hour: currentDateTimeClamped.hour + 1 });
    }
    this.createTreatmentForm.patchValue({
      appointmentDate: currentDateTimeClamped.toJSDate()
    });
    this.feedbackService.feedbackData = null;
  }

  ngOnInit() {
    this.subscriptionManager.call(
      this.customerService.getAllCustomersForTenant(),
      (x => {
        this.customers = x.result;
        this.customersSuggestions = x.result;
        this.subscriptionManager.call(
          this.route.queryParams,
          (params => {
            if (params['calledFromDashboard']) {
              this.calledFromDashboard = true;
            }
            if (params['ownerId']) {
              this.createTreatmentForm.patchValue({
                owner: this.customers.find(y => y.id == params['ownerId']) ?? null
              });
              this.isOwnerSet = true;
              this.subscriptionManager.call(
                this.petService.getAllPetsForCustomer(params['ownerId']),
                (y => {
                  this.pets = y.result;
                  this.petsSuggestions = y.result;
                  this.subscriptionManager.call(
                    this.route.queryParams,
                    (params => {
                      if (params['petId']) {
                        this.createTreatmentForm.patchValue({
                          pet: this.pets.find(z => z.id == params['petId']) ?? null
                        });
                        this.isPetSet = true;
                      } else {
                        if (this.pets.length == 1) {
                          this.createTreatmentForm.patchValue({
                            pet: this.pets[0]
                          });
                          this.isPetSet = true;
                        } else {
                          this.createTreatmentForm.patchValue({
                            pet: null
                          });
                        }
                      }
                    })
                  );
                })
              );
            }
          })
        );
      })
    );
    this.createTreatmentForm.patchValue({ title: this.translationPipe.transform('Treatment.Title.StandardValue') });
  }

  filterCustomers($event: AutoCompleteCompleteEvent) {
    this.customersSuggestions = this.customers.filter(x => (x.firstName + x.lastName).toLocaleLowerCase().includes($event.query.toLocaleLowerCase()));
  }

  getOptionLabelForOwner(owner: CustomerDto): string {
    return owner.firstName + ' ' + owner.lastName + ', ' + owner.street + ' ' + owner.houseNumber + ', ' + owner.postalCode + ' ' + owner.city + ', ' + owner.country;
  }

  filterPets($event: AutoCompleteCompleteEvent) {
    this.petsSuggestions = this.pets.filter(x => x.nickname.toLocaleLowerCase().includes($event.query.toLocaleLowerCase()));
  }

  getOptionLabelForPet = (pet: PetDto): string => {
    if (!this.customers) {
      return pet.nickname; // Fallback, if customers are not loaded yet
    }
    const owner = this.customers.find(x => x.id == pet.ownerId);
    return pet.nickname + ', ' + owner?.firstName + ' ' + owner?.lastName + ', ' + owner?.street + ' ' + owner?.houseNumber + ', ' + owner?.postalCode + ' ' + owner?.city + ', ' + owner?.country;
  };

  OnSelectCustomer($event: AutoCompleteSelectEvent) {
    this.subscriptionManager.call(
      this.petService.getAllPetsForCustomer($event.value.id),
      (x => {
        this.pets = x.result;
        this.petsSuggestions = x.result;
        if (this.pets.length == 1) {
          this.createTreatmentForm.patchValue({
            pet: this.pets[0]
          });
        } else {
          this.createTreatmentForm.patchValue({
            pet: null
          });
        }
      })
    );
  }

  OnSubmit() {
    this.submitButtonClicked = true;
    if (this.createTreatmentForm.valid) {
      this.subscriptionManager.call(
        this.treatmentService.createTreatmentForPet(this.createTreatmentForm.value.pet?.id ?? '', {
            customerId: this.createTreatmentForm.value.owner?.id ?? '',
            appointmentDate: DateTime.fromJSDate(this.createTreatmentForm.value.appointmentDate ?? new Date(0)) ?? DateTime.fromJSDate(new Date(0)),
            images: this.createTreatmentForm.value.images ?? [],
            description: this.createTreatmentForm.value.description ?? '',
            title: this.createTreatmentForm.value.title ?? ''
          } as unknown as CreateTreatmentRequestDto
        ),
        ((x) => {
          if (this.calledFromDashboard) {
            this.router.navigate(['../loggedin/treatment-detail/' + x.result]);
          } else {
            this.router.navigate(['../loggedin/pet-detail/' + this.createTreatmentForm.value.pet?.id]);
          }
        })
      );
    }
  }

  protected readonly isFormControlInvalid = isFormControlInvalid;
}
